import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  FileText, Upload, AlertTriangle, TrendingUp, DollarSign, Clock,
  CheckCircle, XCircle, AlertCircle, Calendar, Building2, Target, BarChart3,
  Loader2, ArrowLeft, RefreshCw, ThumbsUp, ThumbsDown, HelpCircle, Brain,
  FileSearch, Scale, Shield, Gavel, BookOpen, ClipboardList, MessageSquare,
  Network, Download, Eye, ChevronRight, Activity, Zap, ListChecks
} from 'lucide-react';
import api from '@/lib/api';
import { MindMapInteractive } from '@/components/MindMapInteractive';
import { TenderAIChat } from '@/components/TenderAIChat';

const statusColors = {
  DRAFT: 'bg-slate-500/20 text-slate-400',
  PROCESSING: 'bg-blue-500/20 text-blue-400',
  ANALYZED: 'bg-emerald-500/20 text-emerald-400',
  BID_DECISION_PENDING: 'bg-amber-500/20 text-amber-400',
  BID: 'bg-green-500/20 text-green-400',
  NO_BID: 'bg-red-500/20 text-red-400'
};

const recommendationColors = {
  STRONG_BID: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  BID_WITH_CAUTION: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  CONDITIONAL_BID: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  NO_BID: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const riskSeverityColors = {
  CRITICAL: 'bg-red-500/20 text-red-400',
  HIGH: 'bg-orange-500/20 text-orange-400',
  MEDIUM: 'bg-amber-500/20 text-amber-400',
  LOW: 'bg-emerald-500/20 text-emerald-400'
};

const clauseRiskColors = {
  CRITICAL: 'bg-red-500/20 text-red-400',
  HIGH: 'bg-orange-500/20 text-orange-400',
  MEDIUM: 'bg-amber-500/20 text-amber-400',
  LOW: 'bg-emerald-500/20 text-emerald-400',
  ACCEPTABLE: 'bg-slate-500/20 text-slate-400'
};

const complianceStatusColors = {
  COMPLIANT: 'bg-emerald-500/20 text-emerald-400',
  PARTIAL: 'bg-amber-500/20 text-amber-400',
  NON_COMPLIANT: 'bg-red-500/20 text-red-400',
  PENDING_REVIEW: 'bg-slate-500/20 text-slate-400'
};

const formatCurrency = (value, currency = 'USD') => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default function TenderDetail() {
  const navigate = useNavigate();
  const { tenderId } = useParams();
  
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [risks, setRisks] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  const fetchTender = useCallback(async () => {
    try {
      const response = await api.get(`/tenders/${tenderId}`);
      setTender(response.data);
      setPipeline(response.data.processing_pipeline);
    } catch (error) {
      console.error('Error fetching tender:', error);
      toast.error('Failed to load tender');
    } finally {
      setLoading(false);
    }
  }, [tenderId]);

  const fetchRisks = useCallback(async () => {
    try {
      const response = await api.get(`/tenders/${tenderId}/risks`);
      setRisks(response.data || []);
    } catch (error) {
      console.error('Error fetching risks:', error);
    }
  }, [tenderId]);

  const fetchClauses = useCallback(async () => {
    try {
      const response = await api.get(`/tenders/${tenderId}/clauses`);
      setClauses(response.data || []);
    } catch (error) {
      console.error('Error fetching clauses:', error);
    }
  }, [tenderId]);

  const fetchCompliance = useCallback(async () => {
    try {
      const response = await api.get(`/tenders/${tenderId}/compliance`);
      setCompliance(response.data || []);
    } catch (error) {
      console.error('Error fetching compliance:', error);
    }
  }, [tenderId]);

  useEffect(() => {
    fetchTender();
    fetchRisks();
    fetchClauses();
    fetchCompliance();
    
    // Poll for updates if processing
    const interval = setInterval(() => {
      if (tender?.status === 'PROCESSING') {
        fetchTender();
        fetchRisks();
        fetchClauses();
        fetchCompliance();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [fetchTender, fetchRisks, fetchClauses, fetchCompliance, tender?.status]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', 'RFP');

    try {
      setUploading(true);
      await api.post(`/tenders/${tenderId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded. AI analysis started.');
      fetchTender();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleReprocess = async () => {
    try {
      setReprocessing(true);
      await api.post(`/tenders/${tenderId}/reprocess`);
      toast.success('Reprocessing started');
      fetchTender();
    } catch (error) {
      console.error('Error reprocessing:', error);
      toast.error('Failed to reprocess');
    } finally {
      setReprocessing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#1E4E8C] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!tender) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <FileSearch className="w-12 h-12 text-slate-600 mb-4" />
          <p className="text-slate-400">Tender not found</p>
          <Button variant="outline" onClick={() => navigate('/tenders')} className="mt-4 border-[#3A3F4A]">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Tenders
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const bidDecision = tender.bid_decision;
  const riskSummary = tender.risk_summary || {};
  const complianceSummary = tender.compliance_summary || {};
  const financialExposure = tender.financial_exposure || {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tender-detail">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/tenders')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold text-white">{tender.tender_name}</h1>
                <Badge className={statusColors[tender.status]}>{tender.status?.replace(/_/g, ' ')}</Badge>
              </div>
              <p className="text-sm text-slate-400">{tender.client_name} • {tender.tender_reference || tender.project_type}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer">
              <input type="file" accept=".pdf,.docx,.doc,.xlsx,.xls" onChange={handleFileUpload} className="hidden" />
              <Button variant="outline" className="border-[#3A3F4A] text-slate-300" disabled={uploading} asChild>
                <span>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload Document
                </span>
              </Button>
            </label>
            <Button onClick={handleReprocess} className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]" disabled={reprocessing || !tender.documents?.length}>
              {reprocessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
              Re-Analyze
            </Button>
          </div>
        </div>

        {/* Processing Pipeline Progress */}
        {tender.status === 'PROCESSING' && pipeline && (
          <Card className="bg-[#1F2937] border-[#3A3F4A]">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 mb-3">
                <Loader2 className="w-5 h-5 text-[#1E4E8C] animate-spin" />
                <div className="flex-1">
                  <p className="text-sm text-white">AI Analysis in Progress</p>
                  <p className="text-xs text-slate-400">Step {pipeline.current_step || 0} of 14: {pipeline.steps?.[pipeline.current_step - 1]?.step_name || 'Initializing'}</p>
                </div>
                <span className="text-sm text-[#1E4E8C]">{Math.round((pipeline.current_step || 0) / 14 * 100)}%</span>
              </div>
              <Progress value={(pipeline.current_step || 0) / 14 * 100} className="h-2" />
            </CardContent>
          </Card>
        )}

        {/* Bid Decision Banner */}
        {bidDecision && (
          <Card className={`border ${recommendationColors[bidDecision.recommendation] || 'bg-slate-500/20 border-slate-500/30'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${bidDecision.recommendation === 'STRONG_BID' ? 'bg-emerald-500/20' : bidDecision.recommendation === 'NO_BID' ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
                    {bidDecision.recommendation === 'STRONG_BID' ? <ThumbsUp className="w-6 h-6 text-emerald-400" /> :
                     bidDecision.recommendation === 'NO_BID' ? <ThumbsDown className="w-6 h-6 text-red-400" /> :
                     <AlertCircle className="w-6 h-6 text-amber-400" />}
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-white">{bidDecision.recommendation?.replace(/_/g, ' ')}</p>
                    <p className="text-sm text-slate-400">{bidDecision.recommendation_rationale}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-white">{bidDecision.overall_score?.toFixed(0)}</p>
                  <p className="text-xs text-slate-500">Overall Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#1F2937] border border-[#3A3F4A] flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="risks" data-testid="tab-risks">Risks ({risks.length})</TabsTrigger>
            <TabsTrigger value="clauses" data-testid="tab-clauses">Legal ({clauses.length})</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance ({compliance.length})</TabsTrigger>
            <TabsTrigger value="financial" data-testid="tab-financial">Financial</TabsTrigger>
            <TabsTrigger value="comparison" data-testid="tab-comparison">FIDIC Comparison</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report">Report</TabsTrigger>
            <TabsTrigger value="mindmap" data-testid="tab-mindmap">Mind Map</TabsTrigger>
            <TabsTrigger value="questions" data-testid="tab-questions">Clarifications</TabsTrigger>
            <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tender Info */}
              <Card className="bg-[#1F2937] border-[#3A3F4A] lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Tender Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Project Type</p>
                      <p className="text-sm text-white">{tender.project_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Contract Type</p>
                      <p className="text-sm text-white">{tender.contract_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Estimated Value</p>
                      <p className="text-sm text-white">{formatCurrency(tender.estimated_value?.amount, tender.estimated_value?.currency)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Submission Deadline</p>
                      <p className="text-sm text-white">{formatDate(tender.submission_deadline)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Jurisdiction</p>
                      <p className="text-sm text-white">{tender.country_jurisdiction || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Procurement Method</p>
                      <p className="text-sm text-white">{tender.procurement_method}</p>
                    </div>
                  </div>
                  
                  {/* Documents */}
                  <div className="pt-4 border-t border-[#3A3F4A]">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Documents</p>
                    {tender.documents?.length > 0 ? (
                      <div className="space-y-2">
                        {tender.documents.map((doc, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[#0A0E1A]">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-[#1E4E8C]" />
                              <div>
                                <p className="text-sm text-white">{doc.file_name}</p>
                                <p className="text-xs text-slate-500">{doc.page_count || 0} pages • {formatDate(doc.uploaded_at)}</p>
                              </div>
                            </div>
                            <Badge className={doc.processed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                              {doc.processed ? 'Processed' : 'Pending'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No documents uploaded</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Score Components */}
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#1E4E8C]" />
                    Score Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {bidDecision?.score_components?.map((comp, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">{comp.component_name}</span>
                        <span className="text-white">{comp.raw_score?.toFixed(0)}</span>
                      </div>
                      <Progress value={comp.raw_score || 0} className="h-2" />
                      <p className="text-xs text-slate-500 mt-1">Weight: {comp.weight_pct}%</p>
                    </div>
                  )) || <p className="text-sm text-slate-500">No analysis yet</p>}
                </CardContent>
              </Card>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-500/20">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Total Risks</p>
                      <p className="text-xl font-bold text-white">{riskSummary.total_risks || 0}</p>
                      <p className="text-xs text-red-400">{riskSummary.critical_count || 0} critical</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <Gavel className="w-4 h-4 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Legal Clauses</p>
                      <p className="text-xl font-bold text-white">{clauses.length}</p>
                      <p className="text-xs text-orange-400">{clauses.filter(c => c.risk_level === 'CRITICAL' || c.risk_level === 'HIGH').length} high risk</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/20">
                      <Shield className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Compliance</p>
                      <p className="text-xl font-bold text-white">{complianceSummary.compliance_rate?.toFixed(0) || 0}%</p>
                      <p className="text-xs text-red-400">{complianceSummary.non_compliant_count || 0} gaps</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <DollarSign className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Financial Exposure</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(financialExposure.total_financial_commitment)}</p>
                      <p className="text-xs text-slate-500">total commitment</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Risks Tab */}
          <TabsContent value="risks">
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardHeader>
                <CardTitle className="text-white">Risk Register</CardTitle>
                <CardDescription className="text-slate-400">AI-identified risks across 7 domains with severity scoring</CardDescription>
              </CardHeader>
              <CardContent>
                {risks.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No risks identified yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {risks.map((risk, i) => (
                      <motion.div key={risk.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                        className="p-4 rounded-lg bg-[#0A0E1A] border border-[#3A3F4A]">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-slate-500">{risk.risk_id}</span>
                            <Badge className="bg-[#1E4E8C]/20 text-[#1E4E8C]">{risk.domain}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={riskSeverityColors[risk.severity]}>{risk.severity}</Badge>
                            <span className="text-lg font-bold text-white">{risk.risk_score}</span>
                          </div>
                        </div>
                        <p className="text-sm text-white font-medium mb-1">{risk.risk_title}</p>
                        <p className="text-sm text-slate-400 mb-2">{risk.description}</p>
                        {risk.mitigation_strategy && (
                          <div className="p-2 rounded bg-[#1F2937] mt-2">
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Mitigation</p>
                            <p className="text-xs text-slate-300">{risk.mitigation_strategy}</p>
                          </div>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>P: {risk.probability_score}/5</span>
                          <span>I: {risk.impact_score}/5</span>
                          {risk.financial_exposure_usd && <span>Exposure: {formatCurrency(risk.financial_exposure_usd)}</span>}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Legal Clauses Tab */}
          <TabsContent value="clauses">
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardHeader>
                <CardTitle className="text-white">Legal Clause Analysis</CardTitle>
                <CardDescription className="text-slate-400">Contract clauses analyzed against FIDIC standards</CardDescription>
              </CardHeader>
              <CardContent>
                {clauses.length === 0 ? (
                  <div className="text-center py-12">
                    <Gavel className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No clauses analyzed yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clauses.map((clause, i) => (
                      <motion.div key={clause.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                        className="p-4 rounded-lg bg-[#0A0E1A] border border-[#3A3F4A]">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-slate-400">{clause.clause_number}</span>
                            <Badge className="bg-purple-500/20 text-purple-400">{clause.clause_type?.replace(/_/g, ' ')}</Badge>
                          </div>
                          <Badge className={clauseRiskColors[clause.risk_level]}>{clause.risk_level}</Badge>
                        </div>
                        {clause.clause_title && <p className="text-sm text-white font-medium mb-1">{clause.clause_title}</p>}
                        <p className="text-sm text-slate-400 line-clamp-3">{clause.clause_text}</p>
                        {clause.legal_issues?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {clause.legal_issues.map((issue, j) => (
                              <span key={j} className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400">{issue}</span>
                            ))}
                          </div>
                        )}
                        {clause.recommended_revision && (
                          <div className="p-2 rounded bg-[#1F2937] mt-2">
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Recommended Revision</p>
                            <p className="text-xs text-emerald-400">{clause.recommended_revision}</p>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardHeader>
                <CardTitle className="text-white">Compliance Matrix</CardTitle>
                <CardDescription className="text-slate-400">Requirements validation against bidder capabilities</CardDescription>
              </CardHeader>
              <CardContent>
                {compliance.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No compliance items yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {compliance.map((item, i) => (
                      <motion.div key={item.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                        className="p-4 rounded-lg bg-[#0A0E1A] border border-[#3A3F4A]">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-slate-500">{item.requirement_id}</span>
                            <Badge className="bg-blue-500/20 text-blue-400">{item.category}</Badge>
                            {item.mandatory && <Badge className="bg-red-500/20 text-red-400">Mandatory</Badge>}
                          </div>
                          <Badge className={complianceStatusColors[item.status]}>{item.status?.replace(/_/g, ' ')}</Badge>
                        </div>
                        <p className="text-sm text-white mb-1">{item.requirement_text}</p>
                        {item.threshold && <p className="text-xs text-slate-500 mb-2">Threshold: {item.threshold}</p>}
                        {item.gap_description && (
                          <div className="p-2 rounded bg-red-500/10 mt-2">
                            <p className="text-xs text-red-400">{item.gap_description}</p>
                          </div>
                        )}
                        {item.action_required && (
                          <p className="text-xs text-amber-400 mt-2">Action: {item.action_required}</p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardHeader>
                  <CardTitle className="text-white">Financial Exposure Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-lg bg-[#0A0E1A]">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Contract Value</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(financialExposure.contract_value, financialExposure.currency)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-[#0A0E1A]">
                      <p className="text-xs text-slate-500 mb-1">Bid Security</p>
                      <p className="text-lg font-semibold text-white">{formatCurrency(financialExposure.bid_security?.amount)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-[#0A0E1A]">
                      <p className="text-xs text-slate-500 mb-1">Performance Security</p>
                      <p className="text-lg font-semibold text-white">{formatCurrency(financialExposure.performance_security?.amount)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-[#0A0E1A]">
                      <p className="text-xs text-slate-500 mb-1">Retention</p>
                      <p className="text-lg font-semibold text-white">{formatCurrency(financialExposure.retention?.amount)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-[#0A0E1A]">
                      <p className="text-xs text-slate-500 mb-1">Risk Contingency</p>
                      <p className="text-lg font-semibold text-white">{formatCurrency(financialExposure.risk_contingency)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardHeader>
                  <CardTitle className="text-white">LD Exposure</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Maximum LD Exposure</p>
                    <p className="text-2xl font-bold text-red-400">{formatCurrency(financialExposure.ld_exposure?.max_exposure)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-[#0A0E1A]">
                      <p className="text-xs text-slate-500 mb-1">Daily Rate</p>
                      <p className="text-lg font-semibold text-white">{formatCurrency(financialExposure.ld_exposure?.daily_rate)}/day</p>
                    </div>
                    <div className="p-3 rounded-lg bg-[#0A0E1A]">
                      <p className="text-xs text-slate-500 mb-1">Cap Amount</p>
                      <p className="text-lg font-semibold text-white">{formatCurrency(financialExposure.ld_exposure?.cap_amount)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>


          {/* FIDIC Comparison Tab */}
          <TabsContent value="comparison">
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Scale className="w-5 h-5 text-[#1E4E8C]" />
                  FIDIC Clause Comparison
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Comparison of contract clauses against FIDIC standard terms
                </CardDescription>
              </CardHeader>
              <CardContent>
                {tender.clause_comparison_summary ? (
                  <div className="space-y-6">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-[#0A0E1A] rounded-lg p-4 border border-[#3A3F4A]">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Clauses</p>
                        <p className="text-2xl font-bold text-white">{tender.clause_comparison_summary.total_clauses || 0}</p>
                      </div>
                      <div className="bg-[#0A0E1A] rounded-lg p-4 border border-emerald-500/30">
                        <p className="text-xs text-emerald-400 uppercase tracking-wider mb-1">FIDIC Compliant</p>
                        <p className="text-2xl font-bold text-emerald-400">{tender.clause_comparison_summary.fidic_compliant || 0}</p>
                      </div>
                      <div className="bg-[#0A0E1A] rounded-lg p-4 border border-amber-500/30">
                        <p className="text-xs text-amber-400 uppercase tracking-wider mb-1">Minor Deviations</p>
                        <p className="text-2xl font-bold text-amber-400">{tender.clause_comparison_summary.minor_deviations || 0}</p>
                      </div>
                      <div className="bg-[#0A0E1A] rounded-lg p-4 border border-red-500/30">
                        <p className="text-xs text-red-400 uppercase tracking-wider mb-1">Major Deviations</p>
                        <p className="text-2xl font-bold text-red-400">{tender.clause_comparison_summary.major_deviations || 0}</p>
                      </div>
                    </div>
                    
                    {/* Deviations by Type */}
                    {tender.clause_comparison_summary.deviations_by_type && (
                      <div>
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-[#1E4E8C]" />
                          Deviations by Clause Type
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(tender.clause_comparison_summary.deviations_by_type).map(([type, data]) => (
                            <div key={type} className="flex items-center justify-between p-3 rounded-lg bg-[#0A0E1A] border border-[#3A3F4A]">
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${data.high_risk > 0 ? 'bg-red-500' : 'bg-emerald-500'}`} />
                                <span className="text-sm text-white font-medium">{type.replace(/_/g, ' ')}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-xs text-slate-400">{data.count} clause{data.count !== 1 ? 's' : ''}</span>
                                {data.high_risk > 0 && (
                                  <Badge variant="destructive" className="text-xs">
                                    {data.high_risk} High Risk
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Compliance Rate */}
                    <div className="bg-[#0A0E1A] rounded-lg p-4 border border-[#3A3F4A]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-400">FIDIC Compliance Rate</span>
                        <span className="text-lg font-bold text-white">
                          {tender.clause_comparison_summary.total_clauses > 0 
                            ? Math.round((tender.clause_comparison_summary.fidic_compliant / tender.clause_comparison_summary.total_clauses) * 100)
                            : 0}%
                        </span>
                      </div>
                      <Progress 
                        value={tender.clause_comparison_summary.total_clauses > 0 
                          ? (tender.clause_comparison_summary.fidic_compliant / tender.clause_comparison_summary.total_clauses) * 100 
                          : 0} 
                        className="h-2 bg-[#3A3F4A]"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Scale className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">FIDIC comparison not available</p>
                    <p className="text-xs text-slate-500 mt-2">Complete the analysis pipeline to generate comparison</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Executive Report Tab */}
          <TabsContent value="report">
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#1E4E8C]" />
                  Executive Summary Report
                </CardTitle>
                <CardDescription className="text-slate-400">
                  AI-generated executive summary with key findings and recommendations
                </CardDescription>
              </CardHeader>
              <CardContent>
                {tender.executive_summary ? (
                  <div className="space-y-6">
                    {/* Header with Recommendation */}
                    <div className={`p-4 rounded-lg border ${
                      tender.executive_summary.recommendation === 'STRONG_BID' ? 'bg-emerald-500/10 border-emerald-500/30' :
                      tender.executive_summary.recommendation === 'BID_WITH_CAUTION' ? 'bg-amber-500/10 border-amber-500/30' :
                      tender.executive_summary.recommendation === 'CONDITIONAL_BID' ? 'bg-orange-500/10 border-orange-500/30' :
                      'bg-red-500/10 border-red-500/30'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Recommendation</p>
                          <p className={`text-xl font-bold ${
                            tender.executive_summary.recommendation === 'STRONG_BID' ? 'text-emerald-400' :
                            tender.executive_summary.recommendation === 'BID_WITH_CAUTION' ? 'text-amber-400' :
                            tender.executive_summary.recommendation === 'CONDITIONAL_BID' ? 'text-orange-400' :
                            'text-red-400'
                          }`}>
                            {tender.executive_summary.recommendation?.replace(/_/g, ' ')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Overall Score</p>
                          <p className="text-3xl font-bold text-white">{tender.executive_summary.overall_score?.toFixed(0) || 0}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Key Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-[#0A0E1A] rounded-lg p-4 border border-[#3A3F4A]">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Tender</p>
                        <p className="text-sm text-white font-medium">{tender.executive_summary.tender_name}</p>
                        <p className="text-xs text-slate-400 mt-1">{tender.executive_summary.client_name}</p>
                      </div>
                      <div className="bg-[#0A0E1A] rounded-lg p-4 border border-[#3A3F4A]">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Estimated Value</p>
                        <p className="text-lg text-white font-bold">
                          ${(tender.executive_summary.estimated_value?.amount || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Deadline: {tender.executive_summary.submission_deadline 
                            ? new Date(tender.executive_summary.submission_deadline).toLocaleDateString()
                            : 'Not set'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Key Findings */}
                    {tender.executive_summary.key_findings && tender.executive_summary.key_findings.length > 0 && (
                      <div className="bg-[#0A0E1A] rounded-lg p-4 border border-[#3A3F4A]">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <Target className="w-4 h-4 text-[#1E4E8C]" />
                          Key Findings
                        </h3>
                        <ul className="space-y-2">
                          {tender.executive_summary.key_findings.map((finding, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                              <CheckCircle className="w-4 h-4 text-[#1E4E8C] mt-0.5 flex-shrink-0" />
                              {finding}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Financial Summary */}
                    {tender.executive_summary.financial_exposure_summary && (
                      <div className="bg-[#0A0E1A] rounded-lg p-4 border border-[#3A3F4A]">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-amber-400" />
                          Financial Exposure Summary
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-slate-500">Total Commitment</p>
                            <p className="text-sm text-white font-medium">
                              ${(tender.executive_summary.financial_exposure_summary.total_financial_commitment || 0).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Max LD Exposure</p>
                            <p className="text-sm text-red-400 font-medium">
                              ${(tender.executive_summary.financial_exposure_summary.ld_exposure?.max_exposure || 0).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Performance Security</p>
                            <p className="text-sm text-white font-medium">
                              ${(tender.executive_summary.financial_exposure_summary.performance_security?.amount || 0).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Compliance Status</p>
                            <p className="text-sm text-amber-400 font-medium">
                              {tender.executive_summary.compliance_status}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Next Steps */}
                    {tender.executive_summary.next_steps && tender.executive_summary.next_steps.length > 0 && (
                      <div className="bg-gradient-to-r from-[#1E4E8C]/10 to-[#143A6B]/10 rounded-lg p-4 border border-[#1E4E8C]/30">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <ListChecks className="w-4 h-4 text-[#1E4E8C]" />
                          Recommended Next Steps
                        </h3>
                        <ol className="space-y-2">
                          {tender.executive_summary.next_steps.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-sm text-slate-300">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1E4E8C]/20 text-[#1E4E8C] flex items-center justify-center text-xs font-bold">
                                {idx + 1}
                              </span>
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">Executive summary not available</p>
                    <p className="text-xs text-slate-500 mt-2">Complete the analysis pipeline to generate report</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Mind Map Tab */}
          <TabsContent value="mindmap">
            <Card className="bg-[#1F2937] border-[#3A3F4A] overflow-hidden">
              <CardContent className="p-0">
                <MindMapInteractive tenderId={tenderId} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clarification Questions Tab */}
          <TabsContent value="questions">
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardHeader>
                <CardTitle className="text-white">Pre-Bid Clarification Questions</CardTitle>
                <CardDescription className="text-slate-400">AI-generated questions for tender clarification</CardDescription>
              </CardHeader>
              <CardContent>
                {tender.clarification_questions?.length > 0 ? (
                  <div className="space-y-3">
                    {tender.clarification_questions.map((q, i) => (
                      <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                        className="p-4 rounded-lg bg-[#0A0E1A] border border-[#3A3F4A]">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-slate-500">{q.question_id}</span>
                            <Badge className="bg-blue-500/20 text-blue-400">{q.category}</Badge>
                          </div>
                          <Badge className={q.priority >= 4 ? 'bg-red-500/20 text-red-400' : q.priority >= 3 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'}>
                            Priority {q.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-white">{q.question_text}</p>
                        {q.reason && <p className="text-xs text-slate-500 mt-2">Reason: {q.reason}</p>}
                        {q.source_reference && <p className="text-xs text-slate-500">Reference: {q.source_reference}</p>}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No clarification questions generated yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pipeline Tab */}
          <TabsContent value="pipeline">
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardHeader>
                <CardTitle className="text-white">14-Step Processing Pipeline</CardTitle>
                <CardDescription className="text-slate-400">AI analysis workflow status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pipeline?.steps?.map((step, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-[#0A0E1A]">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        step.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-400' :
                        step.status === 'PROCESSING' ? 'bg-blue-500/20 text-blue-400' :
                        step.status === 'FAILED' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-500'
                      }`}>
                        {step.status === 'COMPLETED' ? <CheckCircle className="w-4 h-4" /> :
                         step.status === 'PROCESSING' ? <Loader2 className="w-4 h-4 animate-spin" /> :
                         step.status === 'FAILED' ? <XCircle className="w-4 h-4" /> :
                         <span className="text-xs font-medium">{step.step_number}</span>}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-white">{step.step_name}</p>
                        {step.output_summary && <p className="text-xs text-slate-500">{step.output_summary}</p>}
                        {step.error_message && <p className="text-xs text-red-400">{step.error_message}</p>}
                      </div>
                      <Badge className={
                        step.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-400' :
                        step.status === 'PROCESSING' ? 'bg-blue-500/20 text-blue-400' :
                        step.status === 'FAILED' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }>{step.status}</Badge>
                    </div>
                  )) || (
                    <div className="text-center py-12">
                      <Activity className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">Pipeline not started yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* AI Chat Assistant */}
      <TenderAIChat 
        tenderId={tenderId} 
        tenderName={tender?.tender_name} 
        isAnalyzed={tender?.status === 'ANALYZED' || tender?.status === 'BID_DECISION_PENDING'}
      />
    </DashboardLayout>
  );
}
